// Small replacements for the lodash functions client code used. lodash is CommonJS, so even one
// function pulls shared internals into page bundles. Behaviour matches lodash
// (see collections.test.ts, which compares with it)

type Iteratee<T> = keyof T | ((item: T) => unknown)

const valueOf = <T>(item: T, iteratee: Iteratee<T>) =>
  typeof iteratee === 'function' ? iteratee(item) : item[iteratee]

// Like lodash: values first, then null, then undefined. Strings by code point
function compareValues(a: unknown, b: unknown): number {
  if (a === b) return 0
  if (a === undefined) return 1
  if (b === undefined) return -1
  if (a === null) return 1
  if (b === null) return -1
  return (a as number) > (b as number) ? 1 : (a as number) < (b as number) ? -1 : 0
}

/** Stable sort by one or more keys or functions, each ascending or descending */
export function orderBy<T>(
  list: readonly T[] | null | undefined,
  iteratees: Iteratee<T> | Iteratee<T>[],
  orders: 'asc' | 'desc' | ('asc' | 'desc')[] = 'asc',
): T[] {
  const keys = Array.isArray(iteratees) ? iteratees : [iteratees]
  const dirs = Array.isArray(orders) ? orders : [orders]
  return [...(list ?? [])].sort((a, b) => {
    for (let i = 0; i < keys.length; i++) {
      const result = compareValues(valueOf(a, keys[i]), valueOf(b, keys[i]))
      if (result) return dirs[i] === 'desc' ? -result : result
    }
    return 0
  })
}

export const uniq = <T>(list: readonly T[] | null | undefined): T[] => [...new Set(list ?? [])]

/** Keeps the first item for each key */
export function uniqBy<T>(list: readonly T[] | null | undefined, iteratee: Iteratee<T>): T[] {
  const seen = new Set<unknown>()
  return (list ?? []).filter((item) => {
    const key = valueOf(item, iteratee)
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

export function groupBy<T>(
  list: readonly T[] | null | undefined,
  iteratee: Iteratee<T>,
): Record<string, T[]> {
  const groups: Record<string, T[]> = {}
  for (const item of list ?? []) {
    const key = String(valueOf(item, iteratee))
    ;(groups[key] ??= []).push(item)
  }
  return groups
}

/** Fisher–Yates shuffle into a new array */
export function shuffle<T>(list: readonly T[] | null | undefined): T[] {
  const result = [...(list ?? [])]
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[result[i], result[j]] = [result[j], result[i]]
  }
  return result
}

export function omit<T extends object, K extends keyof T>(object: T, ...keys: K[]): Omit<T, K> {
  const result = { ...object }
  for (const key of keys) delete result[key]
  return result
}

export function pick<T extends object, K extends keyof T>(
  object: T,
  keys: readonly K[],
): Pick<T, K> {
  const result = {} as Pick<T, K>
  for (const key of keys) if (key in object) result[key] = object[key]
  return result
}
